import React, { useState, useMemo, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { getTheme } from './theme/ThemeConfig';
import MainLayout from './layouts/MainLayout';
import Home from './components/Home/Home';

// Route-level code splitting for on-demand bundle loading
const AboutPage = lazy(() => import('./pages/AboutPage'));
const WorkPage = lazy(() => import('./pages/WorkPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const BuildingPage = lazy(() => import('./pages/BuildingPage'));
const WorkArchivePage = lazy(() => import('./pages/WorkArchivePage'));
const WritingPage = lazy(() => import('./pages/WritingPage'));
const BookPage = lazy(() => import('./pages/BookPage'));
const ArticleDetailPage = lazy(() => import('./pages/ArticleDetailPage'));
const ReadingPage = lazy(() => import('./pages/ReadingPage'));
const BookshelfPage = lazy(() => import('./pages/BookshelfPage'));
const BookDetailPage = lazy(() => import('./pages/BookDetailPage'));
const HistoryIdeasPage = lazy(() => import('./pages/HistoryIdeasPage'));
const CodeforcesPage = lazy(() => import('./pages/CodeforcesPage'));
const LearningPage = lazy(() => import('./pages/LearningPage'));
const JourneyPage = lazy(() => import('./pages/JourneyPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ResumePage = lazy(() => import('./pages/ResumePage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
    <CircularProgress size={28} color="secondary" />
  </Box>
);

function App() {
  const [mode, setMode] = useState('dark');

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'dark' ? 'light' : 'dark'));
  };

  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <MainLayout mode={mode} toggleTheme={toggleTheme}>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/work" element={<WorkPage />} />
              <Route path="/work/building" element={<BuildingPage />} />
              <Route path="/work/archive" element={<WorkArchivePage />} />
              <Route path="/work/:slug" element={<ProjectDetailPage />} />
              <Route path="/writing" element={<WritingPage />} />
              <Route path="/writing/book" element={<BookPage />} />
              <Route path="/writing/:slug" element={<ArticleDetailPage />} />
              <Route path="/reading" element={<ReadingPage />} />
              <Route path="/reading/bookshelf" element={<BookshelfPage />} />
              <Route path="/reading/history" element={<HistoryIdeasPage />} />
              <Route path="/history" element={<HistoryIdeasPage />} />
              <Route path="/reading/:slug" element={<BookDetailPage />} />
              <Route path="/codeforces" element={<CodeforcesPage />} />
              <Route path="/learning" element={<LearningPage />} />
              <Route path="/journey" element={<JourneyPage />} />
              <Route path="/resume" element={<ResumePage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </MainLayout>
      </Router>
    </ThemeProvider>
  );
}

export default App;
